import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { type IUser } from 'entities/Users'
import { type ContactsDetailsStore } from 'widgets/ContactsDetails'
import { EnumSyncType } from 'widgets/ContactsDetails/store/types'

type IConversationInfoStoreConfig = {
  onSync?: (type: EnumSyncType.conversation_info) => void
}

export class ConversationInfoStore {
  private _search = ''
  private _open = false
  private _reassigning = false
  private _loading = true
  private _config: IConversationInfoStoreConfig | null = null

  constructor(private _contactsDetailsStore: ContactsDetailsStore) {
    makeAutoObservable(this)
  }

  setConfig = (config: IConversationInfoStoreConfig) => {
    this._config = {
      ...this._config,
      ...config,
    }
  }

  get entities() {
    return this._contactsDetailsStore.entities
  }

  get conversationStore() {
    return this._contactsDetailsStore.entities.conversationStore
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([this.entities.inboxesStore.fetchSmartInboxes({ cache: true })])

    runInAction(() => {
      this._loading = false
    })
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setOpen = (value: boolean) => {
    this._open = value
  }

  setReassigning = (status: boolean) => {
    this._reassigning = status
  }

  onSync = () => {
    this._config?.onSync?.(EnumSyncType.conversation_info)
  }

  onReassign = async (id: number | null) => {
    if (!this.conversation) return

    try {
      runInAction(() => {
        this._reassigning = true
      })

      const data = await this.conversationStore.reassign(this.conversation.id, id)

      if (data) {
        this._contactsDetailsStore.handleUpdateConversation(data)
      }

      runInAction(() => {
        this._reassigning = false
        this.onSync()
      })
    } catch (e) {
      logger.error(e)
    }
  }

  get loading() {
    return this._loading
  }

  get reassigning() {
    return this._reassigning
  }

  get open() {
    return this._open
  }

  get search() {
    return this._search
  }

  get conversation() {
    return this._contactsDetailsStore.conversation
  }

  get inbox() {
    return this.entities.inboxesStore.getItem(this.conversation?.inbox_id)
  }

  get number() {
    return this.entities.numbersStore.getItem(this.conversation?.number_id)
  }

  get members() {
    if (this.inbox?.type !== 'inbox') return []

    return this.inbox.memberIds
      .reduce<IUser[]>((state, id) => {
        const item = this.entities.usersStore.getItem(id)

        return item ? [...state, item] : [...state]
      }, [])
      .filter((member) => !member.isViewOnlyRole)
  }

  get filteredMembers() {
    if (!this._search) return this.members

    return this.members.filter((member) =>
      member.name.toLowerCase().includes(this._search.toLowerCase())
    )
  }
}
